import {
  BookOpenIcon,
  CommandLineIcon,
  PuzzlePieceIcon,
  QueueListIcon,
  ServerStackIcon,
} from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { PageHeader } from '../../components/layout/page/PageHeader';
import { PageShell } from '../../components/layout/page/PageShell';
import { FieldLabel } from '../../components/ui/FieldLabel';
import { getDocumentationCount } from '../../services/documentationApi';
import { getRequestErrorMessage } from '../../utils/getRequestErrorMessage';
import {
  TechnologyOverview,
  getTechnologyApplicationCount,
  getTechnologyCatalogCount,
  getTechnologyIntegrationCount,
  getTechnologyOverview,
  getTechnologyServerCount,
} from '../../services/technologyApi';
import { TechnologyApplicationDistribution } from './TechnologyApplicationDistribution';
import { TechnologyComponentDetailPanel } from './TechnologyComponentDetailPanel';
import { TechnologyInfrastructureFlowMap } from './TechnologyInfrastructureFlowMap';
import { TechnologyOverviewCountCard } from './TechnologyOverviewCountCard';
import { TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS } from './technologyFormOptions';

type OverviewTab = 'mapa' | 'distribuicao';

export function TechnologyOverviewPage() {
  const [overview, setOverview] = useState<TechnologyOverview | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [salesProductId, setSalesProductId] = useState(
    () => searchParams.get('salesProductId') ?? '',
  );
  const [environmentKind, setEnvironmentKind] = useState('');
  const [selectedApplicationId, setSelectedApplicationId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const detailRef = useRef<HTMLDivElement>(null);
  const overviewRequestId = useRef(0);

  const loadOverview = useCallback(async (silent = false) => {
    const requestId = overviewRequestId.current + 1;
    overviewRequestId.current = requestId;
    if (!silent) setLoading(true);
    try {
      const result = await getTechnologyOverview(salesProductId || undefined);
      if (overviewRequestId.current !== requestId) return;
      setOverview(result);
    } catch (error) {
      if (overviewRequestId.current !== requestId) return;
      toast.error(getRequestErrorMessage(error, 'Falha ao carregar a visão geral.'));
    } finally {
      if (overviewRequestId.current === requestId) setLoading(false);
    }
  }, [salesProductId]);

  useEffect(() => {
    void loadOverview();
  }, [loadOverview]);

  const composition = overview?.composition ?? null;
  const selectedApplication = useMemo(() => {
    if (!composition || !selectedApplicationId) return null;
    const applications = [...composition.applications].sort((left, right) =>
      left.name.localeCompare(right.name, 'pt-BR'),
    );
    const index = applications.findIndex((application) => application.id === selectedApplicationId);
    if (index < 0) return null;
    return { application: applications[index], iconIndex: index };
  }, [composition, selectedApplicationId]);

  useEffect(() => {
    if (!selectedApplicationId || !composition) return;
    if (!composition.applications.some((application) => application.id === selectedApplicationId)) {
      setSelectedApplicationId(null);
    }
  }, [composition, selectedApplicationId]);

  useEffect(() => {
    if (!selectedApplicationId) return;
    detailRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [selectedApplicationId]);

  const tab: OverviewTab = searchParams.get('tab') === 'distribuicao' ? 'distribuicao' : 'mapa';

  function selectTab(next: OverviewTab) {
    const params = new URLSearchParams(searchParams);
    if (next === 'mapa') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  }

  return (
    <PageShell>
      <PageHeader
        title="Visão geral"
        description="Números consolidados, mapa de implantação e distribuição das aplicações na infraestrutura."
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
        <TechnologyOverviewCountCard
          label="Inventário"
          icon={QueueListIcon}
          iconClassName="bg-violet-50 text-violet-600"
          load={getTechnologyCatalogCount}
        />
        <TechnologyOverviewCountCard
          label="Aplicações"
          icon={CommandLineIcon}
          iconClassName="bg-sky-50 text-sky-600"
          load={getTechnologyApplicationCount}
        />
        <TechnologyOverviewCountCard
          label="Servidores"
          icon={ServerStackIcon}
          iconClassName="bg-amber-50 text-amber-600"
          load={getTechnologyServerCount}
        />
        <TechnologyOverviewCountCard
          label="Integrações"
          icon={PuzzlePieceIcon}
          iconClassName="bg-indigo-50 text-indigo-600"
          load={getTechnologyIntegrationCount}
        />
        <TechnologyOverviewCountCard
          label="Documentos"
          icon={BookOpenIcon}
          iconClassName="bg-emerald-50 text-emerald-600"
          load={getDocumentationCount}
        />
      </div>

      <div className="erp-tab-bar mt-6 w-fit" role="tablist" aria-label="Visões de tecnologia">
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'mapa'}
          className={`erp-tab ${tab === 'mapa' ? 'erp-tab--active' : 'erp-tab--inactive'}`}
          onClick={() => selectTab('mapa')}
        >
          Mapa da infraestrutura
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={tab === 'distribuicao'}
          className={`erp-tab ${tab === 'distribuicao' ? 'erp-tab--active' : 'erp-tab--inactive'}`}
          onClick={() => selectTab('distribuicao')}
        >
          Distribuição de Aplicações
        </button>
      </div>

      {tab === 'distribuicao' ? (
        <div className="mt-4">
          <TechnologyApplicationDistribution />
        </div>
      ) : (
      <div className="mt-4 flex flex-col gap-4 xl:flex-row xl:items-start">
      <section
        className={`min-w-0 flex-1 rounded-2xl border border-secondary-200 bg-white p-4 shadow-sm ${
          selectedApplication ? 'order-2 xl:order-1' : ''
        }`}
      >
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="text-base font-semibold text-secondary-900">Mapa da infraestrutura</h2>
            <p className="mt-1 text-sm text-secondary-500">
              Visualize onde cada componente está implantado e como se conecta aos recursos.
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-3">
            <label className="erp-label min-w-[16rem]">
              <FieldLabel
                description="Produto cuja composição de aplicações será desenhada no mapa."
                example="Plataforma Go"
              >
                Sistemas
              </FieldLabel>
              <select
                className="erp-input"
                value={salesProductId}
                onChange={(event) => {
                  setSalesProductId(event.target.value);
                  setSelectedApplicationId(null);
                }}
              >
                <option value="">Selecione um sistema</option>
                {(overview?.compositions ?? []).map((option) => (
                  <option key={option.salesProductId} value={option.salesProductId}>
                    {option.name} ({option.applicationCount})
                  </option>
                ))}
              </select>
            </label>
            <label className="erp-label min-w-[12rem]">
              <FieldLabel description="Restringe o fluxo aos ambientes deste tipo." example="Produção">
                Ambientes
              </FieldLabel>
              <select
                className="erp-input"
                value={environmentKind}
                onChange={(event) => setEnvironmentKind(event.target.value)}
              >
                <option value="">Todos os ambientes</option>
                {TECHNOLOGY_ENVIRONMENT_KIND_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </div>

        {loading ? (
          <p className="mt-6 text-sm text-secondary-500">Carregando...</p>
        ) : !salesProductId ? (
          <p className="mt-6 text-sm text-secondary-500">
            Selecione um sistema para ver a implantação.
          </p>
        ) : !composition || composition.applications.length === 0 ? (
          <p className="mt-6 text-sm text-secondary-500">
            Este sistema ainda não tem aplicações. Defina o vínculo em Tecnologia → Sistemas.
          </p>
        ) : (
          <div className="mt-6">
            <TechnologyInfrastructureFlowMap
              composition={composition}
              environmentKind={environmentKind}
              selectedApplicationId={selectedApplicationId}
              onSelectApplication={setSelectedApplicationId}
            />
          </div>
        )}
      </section>
        {selectedApplication && composition ? (
          <div ref={detailRef} className="order-1 w-full shrink-0 xl:sticky xl:top-4 xl:order-2 xl:w-96">
            <TechnologyComponentDetailPanel
              application={selectedApplication.application}
              compositionName={composition.name}
              iconIndex={selectedApplication.iconIndex}
              onClose={() => setSelectedApplicationId(null)}
              onHostingChanged={() => void loadOverview(true)}
            />
          </div>
        ) : null}
      </div>
      )}
    </PageShell>
  );
}
